import { imgUser } from '@/assets/images';
import { Stack } from '@/components/layout';
import { Avatar, SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import GuardianScreenHeader from '@/features/guardian-player/components/guardian-screen-header';
import { PLAYER_PROGRESS_TRACKER_LEVELS } from '@/features/guardian-player/components/player-progress-tracker/constants';
import type {
  LevelSummary,
  PlayerProgressTrackerContentProps,
} from '@/features/guardian-player/components/player-progress-tracker/types';
import { buildLevelSummary } from '@/features/guardian-player/components/player-progress-tracker/utils';
import { useScaling } from '@/hooks';
import { guardianColors } from '@/theme/colors';
import React, { useCallback, useMemo } from 'react';
import { FlatList, Pressable, type ListRenderItem } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import LevelCard from './level-card';
import { useStyles } from './styles';

const strings = STRINGS.guardian.playerProgressTracker;

const getLevelKey = (item: LevelSummary) => String(item.level);

const PlayerProgressTrackerContent = ({
  player,
  onBack,
  onOpenSessionHistory,
  onOpenLevelDetail,
}: PlayerProgressTrackerContentProps) => {
  const styles = useStyles();
  const { scale, isTablet } = useScaling();
  const insets = useSafeAreaInsets();

  const firstName = player.firstname?.trim() || strings.fallbackPlayerName;
  const avatarSource = useMemo(
    () => (player.avatarUrl ? { uri: player.avatarUrl } : imgUser),
    [player.avatarUrl],
  );
  const currentLevel = player.level;
  const levels = useMemo(() => {
    if (!player.progress) return [];

    const progress = player.progress;

    return PLAYER_PROGRESS_TRACKER_LEVELS.map((level, index) =>
      buildLevelSummary(level, currentLevel, progress[index]),
    );
  }, [currentLevel, player.progress]);

  const currentSummary = levels.find(item => item.level === currentLevel) ?? levels[0];
  const percent = Math.round((currentSummary?.progress ?? 0) * 100);

  const handleOpenLevelDetail = useCallback(
    (item: LevelSummary) => {
      onOpenLevelDetail(item.level);
    },
    [onOpenLevelDetail],
  );

  const levelColumnCount = isTablet ? 2 : 1;

  const renderLevelItem = useCallback<ListRenderItem<LevelSummary>>(
    ({ item }) => (
      <Stack style={styles.levelGridItem}>
        <LevelCard item={item} onPress={handleOpenLevelDetail} />
      </Stack>
    ),
    [handleOpenLevelDetail, styles.levelGridItem],
  );

  const listHeader = useMemo(
    () => (
      <Stack>
        <Stack
          style={styles.summaryCard}
          direction="row"
          align="center"
          gap={scale({ phone: 12, tablet: 16 })}>
          <Avatar size={scale(60)} borderWidth={0}>
            <Avatar.Image source={avatarSource} backgroundColor={guardianColors['2']} />
          </Avatar>
          <Stack flex={1} gap={scale({ phone: 8, tablet: 12 })}>
            {isTablet ? (
              <Stack direction="row" align="center" justify="space-between" gap={scale(16)}>
                <Text
                  variant="Font Guardian App/Body/Body 16 Bold"
                  numberOfLines={1}
                  style={styles.summaryTitle}>
                  {strings.currentLevel.replace('{level}', String(currentLevel))}
                </Text>
                <Text
                  variant="Font Guardian App/Subtitle/Subtitle 14 Semibold"
                  numberOfLines={1}
                  style={styles.mutedText}>
                  {strings.percentThrough.replace('{percent}', String(percent))}
                </Text>
              </Stack>
            ) : (
              <>
                <Text
                  variant="Font Guardian App/Subtitle/Subtitle 14 Bold"
                  style={styles.summaryTitle}>
                  {strings.currentLevel.replace('{level}', String(currentLevel))}
                </Text>
                <Text
                  variant="Font Guardian App/Subtitle/Subtitle 12 Semibold"
                  style={styles.mutedText}>
                  {strings.percentThrough.replace('{percent}', String(percent))}
                </Text>
              </>
            )}
            <Stack style={styles.progressTrack}>
              <Stack style={[styles.progressFill, { width: `${percent}%` }]} />
            </Stack>
          </Stack>
        </Stack>

        {isTablet ? (
          <Stack direction="row" align="center" justify="space-between" style={styles.sectionRow}>
            <Text variant="Font Guardian App/Body/Body 16 Bold" style={styles.sectionTitleTablet}>
              {strings.academicProgress.replace('{name}', firstName)}
            </Text>

            <Pressable
              accessibilityRole="button"
              onPress={onOpenSessionHistory}
              style={({ pressed }) => [
                styles.historyButtonTablet,
                pressed ? styles.pressed : null,
              ]}>
              <Text variant="Font Guardian App/Body/Body 16px Regular" style={styles.historyText}>
                {strings.viewFullSessionHistory}
              </Text>
              <SvgIcon name="IcArrowRight" color={guardianColors['Main/Main']} />
            </Pressable>
          </Stack>
        ) : (
          <Text variant="Font Guardian App/Body/Body 16 Bold" style={styles.sectionTitle}>
            {strings.academicProgress.replace('{name}', firstName)}
          </Text>
        )}
      </Stack>
    ),
    [
      avatarSource,
      currentLevel,
      firstName,
      isTablet,
      onOpenSessionHistory,
      percent,
      scale,
      styles.historyButtonTablet,
      styles.historyText,
      styles.mutedText,
      styles.pressed,
      styles.progressFill,
      styles.progressTrack,
      styles.sectionRow,
      styles.sectionTitle,
      styles.sectionTitleTablet,
      styles.summaryCard,
      styles.summaryTitle,
    ],
  );

  const listFooter = useMemo(
    () =>
      !isTablet ? (
        <Pressable
          accessibilityRole="button"
          onPress={onOpenSessionHistory}
          style={({ pressed }) => [styles.historyButton, pressed ? styles.pressed : null]}>
          <Text variant="Font Guardian App/Subtitle/Subtitle 14 Regular" style={styles.historyText}>
            {strings.viewFullSessionHistory}
          </Text>
          <SvgIcon name="IcArrowRight" color={guardianColors['Main/Main']} />
        </Pressable>
      ) : null,
    [isTablet, onOpenSessionHistory, styles.historyButton, styles.historyText, styles.pressed],
  );

  return (
    <Stack flex={1} backgroundColor={guardianColors['1']}>
      <GuardianScreenHeader
        title={strings.screenTitle.replace('{name}', firstName)}
        onBack={onBack}
      />
      <FlatList
        key={isTablet ? 'tablet-level-grid' : 'phone-level-list'}
        data={levels}
        renderItem={renderLevelItem}
        keyExtractor={getLevelKey}
        numColumns={levelColumnCount}
        extraData={isTablet}
        showsVerticalScrollIndicator={false}
        style={styles.levelList}
        contentContainerStyle={[
          styles.scrollContent,
          isTablet ? styles.scrollContentTablet : null,
          { paddingBottom: insets.bottom + scale(14) },
          styles.levelGridContent,
          isTablet ? styles.tabletContent : null,
        ]}
        columnWrapperStyle={isTablet ? styles.levelGridColumn : undefined}
        ListHeaderComponent={listHeader}
        ListFooterComponent={listFooter}
      />
    </Stack>
  );
};

export default PlayerProgressTrackerContent;
